import 'styled-components';

import type { JSX } from 'react';
import { Component, createRef } from 'react';

import type { SearchInputRef } from 'components/ui/forms/shared/SearchInput';
import type { CommonListItem, ListItemType } from 'components/ui/lists/CommonList';

import SearchInput from 'components/ui/forms/shared/SearchInput';
import { ListItems, ListSearchSection } from 'components/ui/lists/CommonList';
import { ElementTestId } from 'enums/testing';

import type { Props as BaseDialogProps } from './Dialog';

import { DialogBodyContentContainer, DialogContentContainer, DialogHeader } from '../layouts/DialogLayout';
import Dialog from './Dialog';

/**
 * If using client-side search, onSearchTermChanged will never be called.
 */
type ClientSideSearchProps = {
  isUsingServerSideSearch?: false;
};

type Props = BaseDialogProps &
  (ClientSideSearchProps | ServerSideSearchProps) & {
    /**
     * The title displayed in the dialog header.
     */
    headerTitle: string;
    /**
     * The list of items to be displayed in the filterable list.
     */
    listItems: CommonListItem[];
    /**
     * An optional React component used to render each list item. Defaults to ListItem.
     */
    renderListItem?: (props: ListItemType) => JSX.Element;
    /**
     * Determines whether the search feature is enabled. Default is true.
     */
    searchEnabled?: boolean;
    /**
     * Placeholder text displayed in the search input field.
     */
    searchPlaceholderText: string;
    /**
     * Optional width for the dialog container.
     */
    width?: number;
  };

/**
 * If using server-side search, onSearchTermChanged must be specified.
 */
type ServerSideSearchProps = {
  isUsingServerSideSearch: true;
  onSearchTermChanged: (query: string) => void;
};

class FilterableListDialog extends Component<Props> {
  static defaultProps = {
    searchEnabled: true,
  };
  state = { searchQuery: '' };
  private searchInputRef;

  constructor(props) {
    super(props);
    this.searchInputRef = createRef<SearchInputRef>();
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { isOpen: isOpenPrev } = prevProps;
    const { isOpen, searchEnabled } = this.props;

    if (searchEnabled) {
      if (!isOpenPrev && isOpen) {
        this.searchInputRef?.focus();
      } else if (isOpenPrev && !isOpen) {
        this.onSearchInputChange('');
      }
    }
  }

  onSearchInputChange = query => {
    if (this.props.isUsingServerSideSearch) {
      this.props.onSearchTermChanged(query);
    } else {
      this.setState({ searchQuery: query });
    }
  };

  render() {
    const { headerTitle, listItems, onCancel, renderListItem, searchEnabled, searchPlaceholderText } = this.props;
    const { searchQuery } = this.state;
    const { width, ...miscProps } = this.props;

    return (
      <Dialog {...miscProps}>
        <DialogContentContainer css={{ minWidth: 306, width }}>
          <DialogHeader onClose={onCancel} showDivider title={headerTitle} />
          {searchEnabled ? (
            <ListSearchSection>
              <SearchInput
                defaultValue={searchQuery}
                onChange={this.onSearchInputChange}
                placeholder={searchPlaceholderText}
                ref={input => {
                  this.searchInputRef = input;
                }}
              />
            </ListSearchSection>
          ) : null}
          <DialogBodyContentContainer data-testid={ElementTestId.FILTER_DIALOG_RESULTS_CONTAINER}>
            <ListItems items={listItems} renderListItem={renderListItem} searchQuery={searchQuery} />
          </DialogBodyContentContainer>
        </DialogContentContainer>
      </Dialog>
    );
  }
}

export default FilterableListDialog;
